<!-- ResourceCategory
        AddEditCategory - modal
        AddEditResource - modal
        ResourceCard
-->
<template>
  <b-container>
    <category-modal />
    <resource-modal />

    <error-indicator v-if="isError" />

    <section v-else>
      <loading-indicator v-if="isLoading" />

      <div v-else>
        <b-card bg-variant="light" class="mt-1">
          <b-card-title>Resource Builder</b-card-title>
          <b-card-sub-title class="mb-2">for {{appName}} app</b-card-sub-title>

          <b-card-text>
            The Resource builder lets you edit the resource page of the Safeharbour app. All changes will be displayed the next time a user opens the app.
            The name and phone number will appear exactly
            as they do below. Add, edit and delete entries with the corresponding buttons. Reorder entries by grabbing the <b-icon-justify></b-icon-justify> and dragging it.
          </b-card-text>
        </b-card>
        <div class="mx-1 my-2">
          <b-button block pill variant="primary" @click="adjustCategory({})">
            <b-icon-cloud-plus-fill></b-icon-cloud-plus-fill>Add New
            <em>Category</em>
          </b-button>
        </div>

        <draggable
          v-model="resourceCategories"
          group="drag-group-category"
          @start="isDragging=true"
          @end="isDragging=false"
          role="tablist"
          v-if="resourceCategories && resourceCategories.length"
          @change="updateCategoryOrder"
          handle=".handle-category"
        >
          <b-card
            no-body
            v-for="(resourceCategory, index) of resourceCategories"
            v-bind:key="resourceCategory.id"
          >
            <b-card-header header-tag="header" class="p-1">
              <b-button
                block
                v-b-toggle="'accordion-' + index"
                variant="dark bg-custom-3"
                class="d-flex"
              >
                <h5>
                  <b-icon-justify class="handle-category"></b-icon-justify>
                  <em>Category:</em>
                  {{resourceCategory.name}}
                </h5>
                <strong aria-hidden="true" class="when-open ml-auto">
                  <b-icon-file-minus></b-icon-file-minus>
                </strong>
                <strong aria-hidden="true" class="when-closed ml-auto">
                  <b-icon-plus-square-fill></b-icon-plus-square-fill>
                </strong>
              </b-button>
            </b-card-header>

            <b-collapse
              :id="'accordion-' + index"
              accordion="my-accordion"
              role="tabpanel"
              class="bg-custom-3"
            >
              <b-button-toolbar
                class="p-1"
                size="sm"
                style="justify-content: center; display: flex;"
              >
                <b-button
                  squared
                  :id="'add-resource-'+ index"
                  variant="success"
                  @click="adjustCategory(resourceCategory)"
                >
                  <b-icon-pencil-square></b-icon-pencil-square>Edit Category
                </b-button>
                <b-button
                  squared
                  class="mx-1"
                  variant="danger"
                  @click="deleteResourceCategory(resourceCategory)"
                >
                  <b-icon-trash></b-icon-trash>Delete Category
                </b-button>
                <b-button
                  squared
                  :id="'add-resource-'+ index"
                  variant="primary"
                  @click="adjustResource({}, resourceCategory)"
                >
                  <b-icon-cloud-plus-fill></b-icon-cloud-plus-fill>Add New
                  <em>Resource</em>
                </b-button>
              </b-button-toolbar>

              <draggable
                v-model="resourceCategory.resources"
                @start="isDragging=true"
                @end="isDragging=false"
                :group="'drag-group-'+resourceCategory.id"
                role="tablist"
                v-if="resourceCategory.resources && resourceCategory.resources.length"
                @change="updateResourceOrder(resourceCategory)"
                handle=".handle-resource"
              >
                <div
                  v-for="resource of resourceCategory.resources"
                  v-bind:key="resource.pk"
                  class="mb-2"
                >
                  <resource-card-vue
                    :resource="resource"
                    :category="resourceCategory"
                    v-on:handle-resource-modal="adjustResource"
                    v-on:nav-to-datapoint="navToDatapoint"
                    v-on:refresh-list="listResourceCategories"
                  />
                </div>
                <p
                  v-if="resourceCategory.resources.length == 0"
                >No Resources for this category. Why not add one?</p>
              </draggable>
            </b-collapse>
          </b-card>
        </draggable>

        <p v-if="resourceCategories.length == 0">No Categories for this app. Why not make one?</p>
      </div>
    </section>
  </b-container>
</template>


<script>
import draggable from "vuedraggable";
import ResourceCardVue from "./ResourceCard.vue";
import CategoryModal from "./CategoryModal.vue";
import ResourceModal from "./ResourceModal.vue";
import LoadingIndicator from "./indicators/LoadingIndicator.vue";
import ErrorIndicator from "./indicators/ErrorIndicator.vue";
import { mapGetters } from "vuex";

import {
  BIconPlusSquareFill,
  BIconFileMinus,
  BIconTrash,
  BIconCloudPlusFill,
  BIconPencilSquare,
  BIconJustify,
} from "bootstrap-vue";

export default {
  components: {
    ResourceCardVue,
    CategoryModal,
    ResourceModal,
    LoadingIndicator,
    ErrorIndicator,
    BIconFileMinus,
    BIconPlusSquareFill,
    BIconTrash,
    BIconCloudPlusFill,
    BIconPencilSquare,
    BIconJustify,
    draggable,
  },

  data: function () {
    return {
      resourceCategories: [],
      isLoading: true,
      isError: false,
      isDragging: false,
    };
  },

  computed: {
    modalObjRes() {
      return this.$store.state.currentResource;
    },
    modalObjCat() {
      return this.$store.state.currentCategory;
    },
    ...mapGetters(["appId", "appName", "curCategory"]),
  },

  created: function () {
    this.listResourceCategories();
  },

  mounted: function () {
    // Listens for a 'hidden' event from the modal

    //this.$root.$on("bv::modal::hidden", (bvEvent, modalId) => {
    this.$root.$on("bv::modal::hidden", () => {
      this.listResourceCategories();
    });
  },

  methods: {
    adjustCategory(category) {
      let categoryCount = 0;
      if (this.resourceCategories) {
        categoryCount = this.resourceCategories.length;
      }
      category = { ...category, categoryCount: categoryCount };
      this.$store.dispatch("category_change", category);
      this.$bvModal.show("modal-add-edit-category");
    },

    adjustResource(resource, category) {
      let resourceCount = 0;
      if (this.resourceCategories.resources) {
        resourceCount = this.resourceCategories.resources.length;
      }

      resource = {
        ...resource,
        category: category.id,
        resourceCount: resourceCount,
      };
      this.$store.dispatch("resource_change", resource);
      this.$bvModal.show("modal-add-edit-resource");
    },

    navToDatapoint(resource, category) {
      this.adjustResource(resource, category);
      this.$router.push("/manage");
    },

    deleteResourceCategory: function (resourceCategory) {
      let deleteMessage = `Are you sure you want to delete ${resourceCategory.name}?`;

      if (resourceCategory.resources.length > 0) {
        deleteMessage =
          deleteMessage +
          ` This wil ALSO delete ${resourceCategory.resources.length} resources and ALL associated datapoints!!!`;
      }

      this.$confirm({
        message: deleteMessage,
        button: {
          no: "No, don't!",
          yes: "Yes really!",
        },
        callback: (confirm) => {
          if (confirm) {
            let deleted = resourceCategory.name;
            this.$http
              .delete(
                `${this.$API_URL}resource_categories/${resourceCategory.id}`
              )
              .then(() => {
                this.$bvToast.toast(
                  `Category: ${deleted} and all related elements deleted successfully.`,
                  {
                    title: "Deletion Succeeded!",
                    variant: "success",
                    toaster: "b-toaster-top-center",
                    autoHideDelay: 5000,
                  }
                );
                this.listResourceCategories();
              })
              .catch((response) => {
                this.$bvToast.toast(
                  `An ${response} has occured. Please try again, but if the error persists an email will automatically be sent to the administrator.`,
                  {
                    title: "Error during submission",
                    variant: "danger",
                    toaster: "b-toaster-top-center",
                    autoHideDelay: 5000,
                  }
                );
              });
          }
        },
      });
    },

    updateCategoryOrder() {
      let putData = [];

      for (let i = 0; i < this.resourceCategories.length; i++) {
        putData.push({
          id: this.resourceCategories[i].id,
          name: this.resourceCategories[i].name,
          order: i,
          owning_group: this.$store.state.currentGroupId,
          app: this.$store.state.currentAppId,
        });
      }

      this.$http
        .put(`${this.$API_URL}resource_categories/bulk/`, putData)
        .then(() => {
          this.$bvToast.toast(`Resource Categories successfully reordered!`, {
            title: "Successful Update",
            variant: "success",
            autoHideDelay: 5000,
          });
        })
        .catch((response) => {
          this.$bvToast.toast(
            `An ${response} has occured. An email has been sent to detailing the problem, but please try again!`,
            {
              title: "Error during submission",
              variant: "danger",
              autoHideDelay: 5000,
            }
          );
        });
    },

    updateResourceOrder(resourceCategory) {
      let putData = [];
      const resources = resourceCategory.resources;

      if (resources && resources.length > 0) {
        for (let i = 0; i < resources.length; i++) {
          putData.push({
            id: resources[i].id,
            name: resources[i].name,
            description: resources[i].description,
            order: i,
            category: resourceCategory.id,
            owning_group: this.$store.state.currentGroupId,
          });
        }

        this.$http
          .put(`${this.$API_URL}resources/bulk/`, putData)
          .then(() => {
            this.$bvToast.toast(`Resources successfully reordered!`, {
              title: "Successful Update",
              variant: "success",
              autoHideDelay: 5000,
            });
          })
          .catch((response) => {
            this.$bvToast.toast(
              `An ${response} has occured. An email has been sent to detailing the problem, but please try again!`,
              {
                title: "Error during submission",
                variant: "danger",
                autoHideDelay: 5000,
              }
            );
          });
      } else {
        console.log("No resources to rearrange");
      }
    },

    listResourceCategories: function () {
      if (this.appId == -1) {
        this.$router.push("/ChooseApp");
        return;
      }

      this.isLoading = true;
      this.$http
        .get(`${this.$API_URL}resource_categories/?app=${this.appId}`)
        .then((response) => {
          this.resourceCategories = response.data;
        })
        .catch((error) => {
          console.log(error);
          //console.log(error.response.data); // returns 404   if no permission
          //this.$router.push("/404");
          this.isError = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.collapsed > .when-open,
.not-collapsed > .when-closed {
  display: none;
}

.bg-custom-3 {
  background-color: #13547a;
}

.centered {
  justify-content: center;
  display: flex;
}
</style>